import { Storages, type IStorageSync } from '@zajno/common/storage';
import { Getter } from '@zajno/common/types/getter';
import type { ExpirationWrapper, ValueWrapper } from './types';

export class StorageUtils {

  constructor(public readonly cacheKeyPrefix: Getter<string>) {}

  /**
   * Constructs new `StorageUtils` with an additional prefix.
   * Empty prefixes are ignored.
   */
  addPrefix(prefix: Getter<string>) {
    return new StorageUtils(() => {
      const currentPrefix = Getter.toValue(this.cacheKeyPrefix);
      const newPrefix = Getter.toValue(prefix);
      return currentPrefix && newPrefix
        ? `${currentPrefix}.${newPrefix}`
        : currentPrefix || newPrefix;
    });
  }

  prefixStorageKey(key: string) {
    const prefix = Getter.toValue(this.cacheKeyPrefix);
    return !prefix ? key : `${prefix}.${key}`;
  }

  /** Wraps a key-taking storage so every key it receives is prefixed. */
  toPrefixed<T>(storage: IStorageSync<T>): IStorageSync<T> {
    return {
      getValue: key => storage.getValue(this.prefixStorageKey(key)),
      setValue: (key, value) => storage.setValue(this.prefixStorageKey(key), value),
      hasValue: key => storage.hasValue(this.prefixStorageKey(key)),
      removeValue: key => storage.removeValue(this.prefixStorageKey(key)),
    };
  }

  toExpirable<T>(storage: IStorageSync<string>, expirationMs?: number) {
    const converted = Storages.toJSONConverted<ExpirationWrapper<T> | null>(storage);

    if (expirationMs == null || isNaN(expirationMs) || expirationMs <= 0) {
      return Storages.toConverted<T | null, ValueWrapper<T> | null>(
        converted,
        raw => raw?.value || null,
        value => {
          if (!value) {
            return null;
          }

          return {
            value,
          };
        },
      );
    }

    return Storages.toConverted<T | null, ExpirationWrapper<T> | null>(
      converted,
      raw => {
        const expiresAt = raw?.exp && +raw.exp;
        if (!expiresAt || (Date.now() >= expiresAt)) {
          // value is expired
          return null;
        }

        return raw.value;
      },
      value => {
        if (!value) {
          return null;
        }

        return {
          exp: Date.now() + expirationMs,
          value,
        };
      },
    );
  }

  clearKeys(storage: IStorageSync<string>) {
    const fullPrefix = Getter.toValue(this.cacheKeyPrefix);
    if (!fullPrefix) {
      // Refuse to wipe the entire backing store on an empty prefix.
      return 0;
    }

    const dirtyHackInnerStorage = storage as unknown as { storage: Storage };
    if (!dirtyHackInnerStorage.storage) {
      return 0;
    }

    const childPrefix = `${fullPrefix}.`;
    const keysToClear: string[] = [];

    for (let i = 0; i < dirtyHackInnerStorage.storage.length; i++) {
      const key = dirtyHackInnerStorage.storage.key(i);
      if (!key) {
        continue;
      }
      if (key === fullPrefix || key.startsWith(childPrefix)) {
        keysToClear.push(key);
      }
    }

    for (const key of keysToClear) {
      storage.removeValue(key);
    }

    return keysToClear.length;
  }
}
